import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * KITCHNEST one-tap "Add" for cart recommendations.
 *
 * Mirrors Horizon's product-form add flow so every cart-items-component (drawer + cart page)
 * re-renders from the Section Rendering API response — the free-shipping bar, subtotal and
 * recommendations all update from real cart state. Guards against double submits.
 */
class KnUpsellAdd extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.error = this.querySelector('.kn-upsell__error');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return [...ids];
  }

  #setBusy(busy) {
    this.#busy = busy;
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', String(busy));
  }

  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
    setTimeout(() => {
      if (this.error) this.error.hidden = true;
    }, 6000);
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#setBusy(true);
    if (this.error) this.error.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: this.#sectionIds().join(','),
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || 'Sorry, this item could not be added.';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'kn-cart-upsell' },
        });
        this.#showError(message);
        this.#setBusy(false);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'kn-cart-upsell',
          sections: data.sections,
          productId: this.dataset.productId,
        },
      });
      document.dispatchEvent(
        new CustomEvent('kitchnest:upsell-added', { detail: { variantId, productId: this.dataset.productId } })
      );
      // The element is usually replaced by the morph; re-enable in case it is kept.
      this.#setBusy(false);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showError('Something went wrong. Please try again.');
      this.#setBusy(false);
    }
  };
}

if (!customElements.get('kn-upsell-add')) customElements.define('kn-upsell-add', KnUpsellAdd);
